import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import moment from "moment";
import posthog from "posthog-js";

/**
 * MainYearCelander Component
 * 
 * This component receives an employee ID via location state (not URL parameters).
 * When navigating from employeesCelanderList.js, the employee ID is passed
 * through location state and can be accessed using this.getEmployeeIdFromState() method.
 * 
 * URL format: /:companyName/:officeName/dashboard/calender
 * 
 * Usage:
 * - this.getEmployeeIdFromState() - gets the employee ID from location state
 * - this.navigateToEmployeeCalendar(employeeId) - navigates to a specific employee's calendar
 * 
 * Note: This component requires an employee ID in location state to function properly.
 * It will not fall back to stored/local storage employee IDs.
 */

import * as timeOffActions from "./timeOffActions";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import classnames from "classnames";
import LeaveDetail from "./calender/yearCalender/leaveDetail";
import YearCalendar from "./calender/yearCalender/yearCalendar";
import TImeOffModal from "./timeOffModal";
import {
  getYearLeaves,
  clearYearLeaves,
  updateDateState,
  updateState,
  getEmployeeId,
  getleaveTypes,
  updateDropDownState,
  getSelectedOffice
} from "../../Global/CustomFunctions";
import { LocalStorage } from "../../Global/Constant";
import { Common, formatMaxAbsentErrors, showMaxAbsentErrors } from "../../../utils";
import { isSubscriptionSuspended, fetchSubscriptionData } from "../../../utils/subscriptionUtils";
import { Container } from "semantic-ui-react";
import FetchingSpinner from "../../common/FetchingSpinner";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
require("./calender/calander.css");
require("./calender/group-styles.css");
require("./mainYearCelander.css");
const clsPrefix = "rc-Month";

class MainYearCelander extends Component {
  constructor(props) {
    super(props);
    // Get employee name and imageUrl from location state
    const employeeName = props.location?.state?.employeeName || null;
    const employeeImageUrl = props.location?.state?.employeeImageUrl || null;
    this.state = {
      totalAvailLeaves: 0,
      date: moment().startOf("year"),
      annualLeaves: props.annualLeaveas,
      isModalOpen: false,
      isFormLoading: false,
      timeOffModal: props.timeOffModal || {},
      timeOffModalErrors: props.timeOffModalErrors || {},
      employeeName: employeeName,
      employeeImageUrl: employeeImageUrl,
      imageLoadError: false,
      startingLeaveOptions: [
        {
          key: 0,
          text: "Morning",
          name: "Morning",
          value: 0,
        },
        {
          key: 1,
          text: "Afternoon",
          name: "Afternoon",
          value: 1,
        },
      ],
      endingLeaveOptions: [
        {
          key: 0,
          text: "LunchTime",
          name: "LunchTime",
          value: 0,
        },
        {
          key: 1,
          text: "End of Day",
          name: "End of Day",
          value: 1,
        },
      ],
      isLoadingLeaveTypes: false,
      isSubscriptionSuspended: false,
    };
  }

  async componentDidMount() {
    // When opened from wall chart, ensure selected office is set so we're in the correct workplace context
    const officeFromState = this.props.location?.state?.office;
    if (officeFromState && typeof officeFromState === "object") {
      const officeJson = JSON.stringify(officeFromState);
      sessionStorage.setItem("selectedOffice", officeJson);
      localStorage.setItem(LocalStorage.selectedOffice, officeJson);
    }

    // Check subscription status
    try {
      const subscriptionData = await fetchSubscriptionData();
      if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
        this.setState({ isSubscriptionSuspended: true });
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("mainYearCalendarSubscription", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
    }

    // Load leave types from storage and fetch year leaves (moved from componentWillMount)
    const leaveTypes = getleaveTypes();
    this.props.timeOff.setLeaveTypes(leaveTypes.leaveTypes);

    const finalEmployeeId = this.getEmployeeIdFromState();
    if (finalEmployeeId) {
      // Avoid double fetch when componentDidMount runs twice (e.g. React Strict Mode)
      if (!this.props.empYearLeavesLoading) {
        this.props.timeOff.getEmpYearLeaves(
          finalEmployeeId,
          null,
          null,
          false,
          this.state.date.year()
        );
      }
    } else {
      console.warn("No employee ID provided in location state. This component requires an employee ID to function properly.");
    }
  }
  countAvailLeaves = data => {
    let count = 0;
    if (data !== undefined)
      Object.keys(data).map(function (k) {
        return (count += data[k]);
      });
    this.setState({
      totalAvailLeaves: count
    });
  };
  componentWillUnmount() {
    this.props.timeOff.clearYearlyLeaves();
    clearYearLeaves();
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
    }
  }

  // Helper method to get the employee ID from location state
  getEmployeeIdFromState = () => {
    const stateEmployeeId = this.props.location?.state?.employeeId;
    return stateEmployeeId;
  };

  // Method to navigate to a specific employee's calendar
  navigateToEmployeeCalendar = (employeeId) => {
    const { companyName, officeName } = this.props.match?.params;
    if (companyName && officeName && employeeId) {
      this.props.history.push({
        pathname: `/${companyName}/${officeName}/dashboard/calender`,
        state: { employeeId: employeeId }
      });
    }
  };

  updateTimeOffModalState = event => {
    let result = updateState(
      event,
      this.state.timeOffModal,
      this.state.timeOffModalErrors
    );
    this.setState({ timeOffModal: result });
  };

  validateLeaveAvailability = timeOffModal => {
    if (
      !timeOffModal.StartDate ||
      !timeOffModal.EndDate ||
      !timeOffModal.EmployeeId ||
      !timeOffModal.LeaveTypeId
    ) {
      return;
    }

    const startDate = new Date(timeOffModal.StartDate);
    const endDate = new Date(timeOffModal.EndDate);
    if (endDate < startDate) {
      return;
    }

    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
    }

    this.validationTimeout = setTimeout(() => {
      this.props.timeOff.validateLeaveAvailability({
        userId: timeOffModal.EmployeeId,
        startDate: timeOffModal.StartDate instanceof Date
          ? timeOffModal.StartDate.toISOString()
          : timeOffModal.StartDate,
        endDate: timeOffModal.EndDate instanceof Date
          ? timeOffModal.EndDate.toISOString()
          : timeOffModal.EndDate,
        startingLeave: timeOffModal.StartingLeave || 0,
        endingLeave: timeOffModal.EndingLeave || 0,
        leaveTypeId: timeOffModal.LeaveTypeId,
        isEdit: timeOffModal.IsEditMode || false,
        existingGroupId: timeOffModal.GroupId || null,
      });
    }, 300);
  };

  handleDateChange = (name, date) => {
    let result = updateDateState(
      name,
      date,
      this.state.timeOffModal,
      this.state.timeOffModalErrors
    );

    let modalToValidate = result;

    // Get the updated timeOffModal state
    const updatedTimeOffModal = { ...this.state.timeOffModal, [name]: date };

    // Check if both dates are selected
    if (updatedTimeOffModal.StartDate && updatedTimeOffModal.EndDate) {
      const startDate = new Date(updatedTimeOffModal.StartDate);
      const endDate = new Date(updatedTimeOffModal.EndDate);

      // Check if it's a single day or multi-day range
      const isSingleDay = startDate.toDateString() === endDate.toDateString();

      if (isSingleDay) {
        // Single Day Logic: Morning and Evening periods apply to the same day
        // No special handling needed for mainYearCelander as it uses the same modal
      } else {
        // Multi-Day Range Logic: 
        // - Morning period applies to From Date (first day)
        // - Evening period applies to To Date (last day)
        // - Days in between are full days
        // Reset EndingLeave to default when switching to multi-day range
        const updatedModal = { ...result, EndingLeave: 0 };
        modalToValidate = updatedModal;
      }
    }

    this.setState({ timeOffModal: modalToValidate });
    this.validateLeaveAvailability(modalToValidate);
  };

  updateDropDownState = (event, data) => {
    let timeOffDetail = { ...this.state.timeOffModal };

    // Check if both dates are selected
    if (timeOffDetail.StartDate && timeOffDetail.EndDate) {
      const startDate = new Date(timeOffDetail.StartDate);
      const endDate = new Date(timeOffDetail.EndDate);
      const isSingleDay = startDate.toDateString() === endDate.toDateString();

      if (data.name === "StartingLeave") {
        if (isSingleDay) {
          // Single Day Logic
          if (data.value === 1) { // Afternoon selected
            // If Afternoon is selected, automatically set Evening to End of Day
            timeOffDetail["StartingLeave"] = 1;
            timeOffDetail["EndingLeave"] = 1;
            this.setState({
              timeOffModal: timeOffDetail,
              endingLeaveOptions: [
                {
                  key: 1,
                  text: "End of Day",
                  name: "End of Day",
                  value: 1,
                },
              ],
            });
            this.validateLeaveAvailability(timeOffDetail);
            return;
          } else {
            // Morning selected - reset ending options
            this.setState({
              endingLeaveOptions: [
                {
                  key: 0,
                  text: "LunchTime",
                  name: "LunchTime",
                  value: 0,
                },
                {
                  key: 1,
                  text: "End of Day",
                  name: "End of Day",
                  value: 1,
                },
              ],
            });
          }
        } else {
          // Multi-Day Range Logic
          // Morning period applies to From Date, Evening period applies to To Date
          // Both can be independently selected
          let result = updateDropDownState(
            event,
            data,
            this.state.timeOffModalErrors
          );
          timeOffDetail[data.name] = result;
          this.setState({
            timeOffModal: timeOffDetail,
            endingLeaveOptions: [
              {
                key: 0,
                text: "LunchTime",
                name: "LunchTime",
                value: 0,
              },
              {
                key: 1,
                text: "End of Day",
                name: "End of Day",
                value: 1,
              },
            ],
          });
          this.validateLeaveAvailability(timeOffDetail);
          return;
        }
      }

      if (data.name === "EndingLeave") {
        if (isSingleDay) {
          // Single Day Logic: Evening period applies to the same day
          let result = updateDropDownState(
            event,
            data,
            this.state.timeOffModalErrors
          );
          timeOffDetail[data.name] = result;
          this.setState({ timeOffModal: timeOffDetail });
          this.validateLeaveAvailability(timeOffDetail);
          return;
        } else {
          // Multi-Day Range Logic: Evening period applies to To Date (last day)
          let result = updateDropDownState(
            event,
            data,
            this.state.timeOffModalErrors
          );
          timeOffDetail[data.name] = result;
          this.setState({ timeOffModal: timeOffDetail });
          this.validateLeaveAvailability(timeOffDetail);
          return;
        }
      }
    }

    // Default case for other fields
    let result = updateDropDownState(
      event,
      data,
      this.state.timeOffModalErrors
    );
    timeOffDetail[data.name] = result;
    this.setState({ timeOffModal: timeOffDetail });
    this.validateLeaveAvailability(timeOffDetail);
  };

  bookTimeOff = event => {
    event.preventDefault();

    // Check if subscription is suspended
    if (this.state.isSubscriptionSuspended) {
      let roleId = this.props.roleId || this.props.UserRoleId;
      if (!roleId) {
        try {
          const { WebStorage, WebStorageNames } = require("../../../utils");
          roleId = WebStorage.getLocalStore(WebStorageNames.RoleId);
        } catch (e) {
          roleId = localStorage.getItem("roleId");
        }
      }
      const isAdmin = roleId && (parseInt(roleId) === 1 || parseInt(roleId) === 2);

      if (isAdmin) {
        Common.showErrorMessage("Your subscription has been suspended. Please update your payment information to book leaves.");
      } else {
        Common.showErrorMessage("Access is temporarily unavailable. Your company's subscription is currently suspended. Please contact your administrator.");
      }
      return;
    }

    this.setState({ isFormLoading: true });
    let empId = this.getEmployeeIdFromState();

    // Check if it's edit mode
    const isEditMode = this.state.timeOffModal.IsEditMode || false;

    // Choose the appropriate action based on edit mode
    const action = isEditMode
      ? this.props.timeOff.updateLeave
      : this.props.timeOff.bookEmployeeTimeOff;

    action(this.state.timeOffModal)
      .then(response => {
        if (response.success) {
          // Check for MaxAbsent errors in the response
          if (response.data && response.data.skippedDatesWithMessages) {
            showMaxAbsentErrors(response.data.skippedDatesWithMessages, "Some dates were skipped due to MaxAbsent limit");
          }

          // Show success message if there are booked dates
          if (response.data && response.data.bookedDates && response.data.bookedDates.length > 0) {
            const bookedCount = response.data.bookedDates.length;
            Common.showSuccessMessage(
              `Leave successfully booked for ${bookedCount} ${bookedCount === 1 ? 'date' : 'dates'}.`
            );
          }

          this.props.timeOff.getEmpYearLeaves(
            empId,
            null,
            null,
            false,
            this.state.date.year() // Pass the current year
          );

          this.setState({ isModalOpen: false, isFormLoading: false });
        } else {
          this.setState({ isFormLoading: false });
          Common.showErrorMessage(response.message);
        }
      })
      .catch(error => {
        this.setState({ isFormLoading: false });
        Common.showErrorMessage(error);
      });
  };
  clearModel = () => {
    let modal = {
      StartDate: "",
      EndDate: "",
      LeaveNote: "",
      LeaveTypeId: null,
      StartingLeave: 0,
      EndingLeave: 0,
      EmployeeId: this.getEmployeeIdFromState() || "",
      IsEditMode: false,
      GroupId: null,
      // Clear public holiday fields
      IsPublicHoliday: false,
      PublicHolidayName: "",
      TotalDays: 1,
      Approvals: []
    };
    this.setState({ timeOffModal: modal });
  };

  isCurrentUserAdmin = () => {
    let roleId = this.props.roleId || this.props.UserRoleId;
    if (!roleId) {
      try {
        const { WebStorage, WebStorageNames } = require("../../../utils");
        roleId = WebStorage.getLocalStore(WebStorageNames.RoleId);
      } catch (e) {
        roleId = localStorage.getItem("roleId");
      }
    }
    return roleId && (parseInt(roleId) === 1 || parseInt(roleId) === 2);
  };

  getCalendarRoleId = () => {
    let roleId = this.props.roleId || this.props.UserRoleId;
    if (!roleId) {
      try {
        const { WebStorage, WebStorageNames } = require("../../../utils");
        roleId = WebStorage.getLocalStore(WebStorageNames.RoleId);
      } catch (e) {
        roleId = localStorage.getItem("roleId");
      }
    }
    return parseInt(roleId, 10);
  };

  renderLeaveYearHeaderDescription = () => {
    const { t } = this.props;
    const roleId = this.getCalendarRoleId();
    const desc =
      roleId === 3
        ? t("app.myCalendar.descriptionManager")
        : roleId === 4
          ? t("app.myCalendar.descriptionEmployee")
          : t("app.myCalendar.descriptionAdmin");

    const roleLabel =
      roleId === 1 || roleId === 2
        ? t("app.myCalendar.admin")
        : roleId === 3
          ? t("app.myCalendar.manager")
          : t("app.myCalendar.employee");

    return (
      <>
        {this.state.employeeName ? (
          <span className="d-block mb-2" style={{ fontSize: "13px", color: "#6b7280" }}>
            <i className="bi bi-person me-1" />
            <strong style={{ color: "#1f2937" }}>{this.state.employeeName}</strong>
            <span style={{ marginLeft: "8px", color: "#9ca3af" }}>{roleLabel}</span>
          </span>
        ) : null}
        <span>{desc}</span>
      </>
    );
  };

  handleOpenModal = (date, employeeId, groupId = null) => {
    // Check if user has permission to view/edit this employee's leaves
    // Note: isViewable is at employee level, not individual leave level
    // For year calendar, we need to check if the current employee is viewable
    // This would typically come from a user/employee context or props

    // For now, we'll allow the modal to open since the permission check
    // should be handled at the data level (employeeLeaves with isViewable)
    // The actual permission enforcement happens in the onClick handlers

    // Close any existing modal first
    this.setState({ isModalOpen: false });

    // Clear modal state completely
    this.clearModel();
    this.props.timeOff.clearTimeOffModal();
    this.props.timeOff.clearValidationResult();

    // Initialize modal state properly - don't spread old state, create fresh
    const finalEmployeeId = this.getEmployeeIdFromState() || employeeId;
    let localState = {
      EmployeeId: finalEmployeeId,
      StartDate: date ? (date instanceof Date ? date.toISOString() : date) : "",
      EndDate: date ? (date instanceof Date ? date.toISOString() : date) : "",
      StartingLeave: 0,
      EndingLeave: 0,
      LeaveNote: "",
      LeaveTypeId: null,
      IsEditMode: false,
      GroupId: null,
      // Explicitly clear public holiday fields
      IsPublicHoliday: false,
      PublicHolidayName: "",
      TotalDays: 1,
      Approvals: []
    };

    // Check if this is a public holiday click (groupId contains PUBLIC_HOLIDAY)
    const isPublicHolidayClick = groupId && (groupId.toString().includes('PUBLIC_HOLIDAY') || groupId.toString().includes('public_holiday'));

    // If groupId is provided, it's edit mode or public holiday view
    if (groupId) {
      localState.GroupId = groupId;
      localState.IsEditMode = true;

      // Fetch leave details and leave types for edit mode
      this.setState({
        timeOffModal: localState,
        isLoadingLeaveTypes: true,
      });

      // Load existing leave data and leave types
      Promise.all([
        this.props.timeOff.getLeaveByGroupId(groupId, finalEmployeeId),
        this.props.timeOff.getLeaveTypesByEmployee(finalEmployeeId)
      ])
        .then(([leaveData, leaveTypes]) => {
          // Populate the modal with existing leave data
          const updatedState = {
            ...localState,
            StartDate: leaveData.startDate ? new Date(leaveData.startDate).toISOString() : localState.StartDate,
            EndDate: leaveData.endDate ? new Date(leaveData.endDate).toISOString() : localState.EndDate,
            StartingLeave: leaveData.startingLeave || 0,
            EndingLeave: leaveData.endingLeave || 0,
            LeaveTypeId: leaveData.leaveTypeId || null,
            LeaveNote: leaveData.leaveNote || "",
            TotalDays: leaveData.totalDays || 1,
            Approvals: leaveData.approvals || [],
            // Public holiday fields - only set if it's actually a public holiday
            IsPublicHoliday: isPublicHolidayClick || leaveData.isPublicHoliday || false,
            PublicHolidayName: isPublicHolidayClick
              ? (leaveData.publicHolidayName || "")
              : (leaveData.isPublicHoliday ? (leaveData.publicHolidayName || "") : ""),
            IsAutoDeducted: !!leaveData.isAutoDeducted,
            AutoDeductionReason: leaveData.autoDeductionReason ?? null,
          };

          this.setState({
            timeOffModal: updatedState,
            isModalOpen: true,
            isLoadingLeaveTypes: false,
            endingLeaveOptions: [
              {
                key: 0,
                text: "LunchTime",
                name: "LunchTime",
                value: 0,
              },
              {
                key: 1,
                text: "End of Day",
                name: "End of Day",
                value: 1,
              },
            ],
          });
        })
        .catch((error) => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("mainYearCalendarLeaveData", {
              type: "exception",
              success: false,
              reason: error?.response?.data?.message ?? error?.message ?? "Error loading leave data or types",
              status: error?.response?.status,
            });
          }

          // If it's a public holiday click, try to get public holiday info from props
          if (isPublicHolidayClick && this.props.publicHolidays && date) {
            const clickedDate = date instanceof Date ? date : new Date(date);
            const publicHoliday = this.props.publicHolidays.find(ph => {
              const phDate = new Date(ph.date);
              return phDate.getDate() === clickedDate.getDate() &&
                phDate.getMonth() === clickedDate.getMonth() &&
                phDate.getFullYear() === clickedDate.getFullYear();
            });

            if (publicHoliday) {
              // Open modal with public holiday information - clear all other fields
              const publicHolidayState = {
                ...localState,
                IsPublicHoliday: true,
                PublicHolidayName: publicHoliday.name || publicHoliday.holidayName || "Public Holiday",
                LeaveTypeId: null, // Clear leave type for public holidays
                LeaveNote: "", // Clear leave note
                StartingLeave: 0,
                EndingLeave: 0,
              };
              this.setState({
                timeOffModal: publicHolidayState,
                isModalOpen: true,
                isLoadingLeaveTypes: false,
                endingLeaveOptions: [
                  {
                    key: 0,
                    text: "LunchTime",
                    name: "LunchTime",
                    value: 0,
                  },
                  {
                    key: 1,
                    text: "End of Day",
                    name: "End of Day",
                    value: 1,
                  },
                ],
              });
              return;
            }
          }

          // Fallback: open modal with basic data
          this.setState({
            timeOffModal: localState,
            isModalOpen: true,
            isLoadingLeaveTypes: false,
            endingLeaveOptions: [
              {
                key: 0,
                text: "LunchTime",
                name: "LunchTime",
                value: 0,
              },
              {
                key: 1,
                text: "End of Day",
                name: "End of Day",
                value: 1,
              },
            ],
          });
        });
    } else {
      // This is a new leave - ensure public holiday fields are cleared
      localState.IsPublicHoliday = false;
      localState.PublicHolidayName = "";

      // This is a new leave - fetch leave types first
      this.setState({
        timeOffModal: localState,
        isLoadingLeaveTypes: true,
      });

      // Fetch leave types for this specific employee
      this.props.timeOff.getLeaveTypesByEmployee(finalEmployeeId)
        .then(() => {
          // Open modal after leave types are loaded
          this.setState({
            isModalOpen: true,
            isLoadingLeaveTypes: false,
            endingLeaveOptions: [
              {
                key: 0,
                text: "LunchTime",
                name: "LunchTime",
                value: 0,
              },
              {
                key: 1,
                text: "End of Day",
                name: "End of Day",
                value: 1,
              },
            ],
          });
        })
        .catch((error) => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("mainYearCalendarLeaveTypes", {
              type: "exception",
              success: false,
              reason: error?.response?.data?.message ?? error?.message ?? "Error loading leave types",
              status: error?.response?.status,
            });
          }
          // Still open modal even if leave types fail to load
          this.setState({
            isModalOpen: true,
            isLoadingLeaveTypes: false,
            endingLeaveOptions: [
              {
                key: 0,
                text: "LunchTime",
                name: "LunchTime",
                value: 0,
              },
              {
                key: 1,
                text: "End of Day",
                name: "End of Day",
                value: 1,
              },
            ],
          });
        });
    }
  };

  handleCloseModal = () => {
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
      this.validationTimeout = null;
    }
    this.setState({ isModalOpen: false });
    this.props.timeOff.clearValidationResult();
  };

  handlePrevYear = e => {
    e.preventDefault();
    const newYear = this.state.date.clone().subtract(1, "year");
    this.setState({ date: newYear });

    // Fetch data for the new year
    const finalEmployeeId = this.getEmployeeIdFromState();

    if (finalEmployeeId) {
      this.props.timeOff.getEmpYearLeaves(
        finalEmployeeId,
        null,
        null,
        false,
        newYear.year() // Pass the year parameter
      );
    }
  };

  handleNextYear = e => {
    e.preventDefault();
    const newYear = this.state.date.clone().add(1, "year");
    this.setState({ date: newYear });

    // Fetch data for the new year
    const finalEmployeeId = this.getEmployeeIdFromState();

    if (finalEmployeeId) {
      this.props.timeOff.getEmpYearLeaves(
        finalEmployeeId,
        null,
        null,
        false,
        newYear.year() // Pass the year parameter
      );
    }
  };

  render() {
    // Map leave types and preserve icon and deducted properties
    let allLeaveTypes = this.props.leaveTypes?.map(function (option) {
      // Handle deducted property - convert to boolean and default to true (deductible)
      let deducted = true; // Default to deductible
      if (option.deducted !== undefined && option.deducted !== null) {
        // Handle boolean, string, or number values
        deducted = option.deducted === true || option.deducted === 'true' || option.deducted === 1 || option.deducted === '1';
      }

      return {
        key: option.id,
        text: option.name,
        name: option.name,
        value: option.id,
        leavesCount: option.leavesCount || 0,
        leaveDivision: option.leaveDivision === 0 ? "Days" : "Hours",
        icon: option.icon || null,
        deducted: deducted,
      };
    }) || [];

    // Separate deductible and non-deductible leaves (deducted === true means deductible)
    const deductibleLeaves = allLeaveTypes.filter(lt => lt.deducted === true);
    const nonDeductibleLeaves = allLeaveTypes.filter(lt => lt.deducted === false);

    // Sort each group
    deductibleLeaves.sort((a, b) => a.key - b.key);
    nonDeductibleLeaves.sort((a, b) => a.key - b.key);

    // Create grouped options: deductible first, then header, then non-deductible
    let leaveTypeOptions = [...deductibleLeaves];

    // Add header separator for non-deductible leaves if they exist
    if (nonDeductibleLeaves.length > 0) {
      leaveTypeOptions.push({
        key: 'non-deductible-header',
        text: 'Non-Deductible Leaves',
        value: 'non-deductible-header',
        disabled: true,
        className: 'leave-type-header',
        isHeader: true
      });
      leaveTypeOptions.push(...nonDeductibleLeaves);
    }

    const { annualLeaves, totalAvailLeaves } = this.state;
    const { leaveTypesCount } = this.props;

    // Get employee ID for the calendar (from location state)
    const finalEmployeeId = this.getEmployeeIdFromState();

    // Check if we have an employee ID
    if (!finalEmployeeId) {
      return (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h3 className="text-muted">No Employee Selected</h3>
            <p className="text-muted">Please select an employee from the employee list to view their calendar.</p>
          </div>
        </div>
      );
    }

    return (
      <Container className="py-4 px-3 px-md-4" fluid>
        <div>
          <TImeOffModal
            IsModalOpen={this.state.isModalOpen}
            handleCloseModal={this.handleCloseModal}
            onChange={this.updateTimeOffModalState}
            handleDateChange={this.handleDateChange}
            onSave={this.bookTimeOff}
            timeOffModal={this.state.timeOffModal || {}}
            updateDropDown={this.updateDropDownState}
            leaveTypeOptions={leaveTypeOptions}
            isFormLoading={this.state.isFormLoading}
            startingLeaveOptions={this.state.startingLeaveOptions}
            endingLeaveOptions={this.state.endingLeaveOptions}
            isLoadingLeaveTypes={this.state.isLoadingLeaveTypes}
            isEditMode={this.state.timeOffModal?.IsEditMode || false}
            validation={this.props.validation}
            validateLeaveAvailability={this.validateLeaveAvailability}
            isAdmin={this.isCurrentUserAdmin()}
          />
          {/* Header — CommonPageHeader (animated bar) + year navigation */}
          <div className="main-year-cal-header-row">
            <div className="main-year-cal-header-main">
              <CommonPageHeader
                barClassName="yearcalendar-header-bar"
                contentClassName="office-header-content"
                leftClassName="office-header-left"
                iconWrapperClassName="office-header-icon-wrapper"
                textClassName="office-header-text"
                titleClassName="office-page-title"
                descriptionClassName="office-page-description"
                icon={<i className="bi bi-calendar3" style={{ fontSize: "20px" }} />}
                title={this.props.t("app.myCalendar.title")}
                description={this.renderLeaveYearHeaderDescription()}
                onBack={() => this.props.history.goBack()}
                backLabel={this.props.t("app.myCalendar.back")}
              />
            </div>
            <div className="main-year-cal-header-year">
              <div className="year-navigation d-flex align-items-center">
                <button
                  className="year-nav-btn btn btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center"
                  onClick={this.handlePrevYear}
                  type="button"
                  aria-label="Previous Year"
                >
                  <i className="bi bi-chevron-left fs-5"></i>
                </button>
                <div className="year-display mx-3">
                  <div className="year-text text-center">
                    <div className="year-label text-muted small mb-1">Year</div>
                    <div className="year-value text-custom-primary fw-bold fs-4">
                      {this.state.date.format("YYYY")}
                    </div>
                    <div className="year-range text-muted small mt-1">
                      January - December
                    </div>
                  </div>
                </div>
                <button
                  className="year-nav-btn btn btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center"
                  onClick={this.handleNextYear}
                  type="button"
                  aria-label="Next Year"
                >
                  <i className="bi bi-chevron-right fs-5"></i>
                </button>
              </div>
            </div>
          </div>
          <div className="timeTestic-container" style={{ position: "relative", minHeight: "400px" }}>
            {this.props.empYearLeavesLoading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.8)",
                  zIndex: 10,
                  borderRadius: "8px",
                }}
              >
                <FetchingSpinner text={this.props.t("app.myCalendar.loadingCalendar")} />
              </div>
            )}
            <YearCalendar
              startDate={this.state.date}
              endDate={this.state.date.clone().endOf("year")}
              empLeaves={this.props.empYearLeaves}
              weekends={(() => {
                const office = getSelectedOffice();
                return office?.id ? (this.props.officeBusinessDays[office.id] || 62) : 62;
              })()}
              handleOpenModal={this.handleOpenModal}
              employeeId={finalEmployeeId}
              publicHolidays={this.props.publicHolidays || []}
            />

            <LeaveDetail
              year={this.state.date._d.getFullYear()}
              annualLeaves={annualLeaves}
              leaveTypesCount={leaveTypesCount}
              totalAvailLeaves={totalAvailLeaves}
              remaingDays={this.props.leaveAllowanceSummary?.totalRemainingDays ?? (annualLeaves - this.props.empYearLeaves?.length)}
              empYearLeaves={this.props.empYearLeaves}
              publicHolidays={this.props.publicHolidays || []}
              leaveTypes={this.props.leaveTypes || []}
              leaveAllowanceSummary={this.props.leaveAllowanceSummary}
              noDeductibleLeaveTypesInfo={this.props.noDeductibleLeaveTypesInfo}
              annualTrends={this.props.annualTrends || []}
              timeOffs={this.props.timeOffs}
              pieChartData={this.props.pieChartData || []}
            />

          </div>
        </div>
      </Container>
    );
  }
}

const mapStateToProps = ({ timeOffReducer, officeReducer }) => {
  const {
    empYearLeaves,
    empYearLeavesLoading,
    weekends,
    timeOffModal,
    leaveTypesCount,
    leaveTypes,
    timeOffModalErrors,
    annualLeaveas,
    publicHolidays,
    leaveAllowanceSummary,
    noDeductibleLeaveTypesInfo,
    annualTrends,
    timeOffs,
    pieChartData,
    validation
  } = timeOffReducer;

  const { officeBusinessDays } = officeReducer;

  return {
    empYearLeavesLoading,
    weekends,
    timeOffModal,
    timeOffModalErrors,
    leaveTypes,
    leaveTypesCount,
    annualLeaveas,
    empYearLeaves,
    publicHolidays,
    officeBusinessDays,
    leaveAllowanceSummary,
    noDeductibleLeaveTypesInfo,
    annualTrends,
    timeOffs,
    pieChartData,
    validation
  };
};
const mapDispatchToProps = dispatch => {
  return {
    timeOff: bindActionCreators(timeOffActions, dispatch)
  };
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(MainYearCelander));
